import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import toast from "react-hot-toast";

import Axios from "../../services/axios";
import moment from "moment";
import { fetchData } from "../../helpers/fetchData";
import { handleCheckPatientElibility } from "../../services/ApiMethods";
import { useState } from "react";
import { baseApi } from "../api/baseApi";

export const getPatientsData = createAsyncThunk(
  "getPatientsList",
  async (payload, { rejectWithValue }) => {
    try {
      const sanitizedPayload = { ...payload };

      if (sanitizedPayload?.old_file_no) {
        const val = Number(sanitizedPayload?.old_file_no);
        if (val) {
          sanitizedPayload.old_file_no = val;
        } else {
          delete sanitizedPayload.old_file_no;
        }
      } else {
        delete sanitizedPayload.old_file_no;
      }

      if (sanitizedPayload?.file_no) {
        const val = sanitizedPayload.file_no;
        if (val) {
          sanitizedPayload.file_no = val;
        } else {
          delete sanitizedPayload.file_no;
        }
      } else {
        delete sanitizedPayload.file_no;
      }

      const response = await Axios.post(`/v3/patient/getList`, sanitizedPayload);
      return response.data;
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Error"
      );
      return rejectWithValue(error?.response?.data?.error || "Error");
    }
  }
);

export const createPatient = createAsyncThunk(
  "createPatient",
  async (
    {
      payload,
      navigate,
      checkEligibility,
      setOpenEligibilityForm,
      setEligibilityResData,
      setCheckEligibility,
      returnDetail,
      fromVisit,
      setDocId,
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await Axios.post(`/v3/patient/create`, payload);
      toast.success(response?.data?.message);
      setDocId(response?.data?.patient?.document_id);
      dispatch(baseApi.util.invalidateTags(["Beneficiary"]));
      if (checkEligibility) {
        setCheckEligibility(false);

        const payloads = response?.data?.patient?.insurance_plans?.map(
          (data) => ({
            payer_id: data?.payer_id,
            rcm_ref: response?.data?.patient?.rcm_ref,
            insurance_plan_id: data?.insurance_plan_id,
            is_new_born:
              payload?.is_new_born ??
              response?.data?.patient?.is_new_born ??
              false,
          })
        );

        const results = await Promise.all(
          payloads.map((data) =>
            handleCheckPatientElibility(
              data,
              () => { },
              () => { }
            )
          )
        );

        const validResults = results.filter((r) => r);
        setEligibilityResData(validResults);
        setOpenEligibilityForm(true);

        // setCheckEligibility(false);
        // const checkEligibilityPayload = {
        //   payer_id: response?.data?.patient?.insurance_plans?.length
        //     ? response?.data?.patient?.insurance_plans?.[0]?.payer_id
        //     : "",
        //   rcm_ref: response?.data?.patient?.rcm_ref,
        //   insurance_plan_id: response?.data?.patient?.insurance_plans?.length
        //     ? response?.data?.patient?.insurance_plans?.[
        //         response?.data?.patient?.insurance_plans?.length - 1
        //       ]?.insurance_plan_id
        //     : "",
        // };
        // await handleCheckPatientElibility(
        //   checkEligibilityPayload,
        //   setOpenEligibilityForm,
        //   setEligibilityResData
        // );
      }
      if (checkEligibility == false) {
        fromVisit
          ? navigate(
            returnDetail?.is_without_slot == true
              ? "/create-visit-without-slot"
              : "/create-visit",
            {
              state: {
                ...returnDetail,
                doc_id: response?.data?.patient?.document_id,
              },
            }
          )
          : navigate("/beneficiary");
      }
    } catch (error) {
      setCheckEligibility(false);
      toast.error(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Error"
      );
      return rejectWithValue(error?.response?.data?.error || "Error");
    }
  }
);

export const updatePatient = createAsyncThunk(
  "updatePatient",
  async (
    {
      payload,
      navigate,
      getBeneficiariesPayload,
      checkEligibility,
      setOpenEligibilityForm,
      setEligibilityResData,
      setCheckEligibility,
      returnDetail,
      fromVisit,
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const elIds = payload?.insurance_plans
        ?.filter((el) => el?.check_eligible === true)
        .map((el) => el.his_payer_id);
      const response = await Axios.post(`/v3/patient/update`, payload);

      toast.success(response?.data?.message);
      dispatch(baseApi.util.invalidateTags(["Beneficiary"]));
      if (checkEligibility) {
        setCheckEligibility(false);
        const plansToFilter = response?.data?.patient?.insurance_plans || [];
        const payloads = plansToFilter
          ?.filter((el) => (checkEligibility ? true : elIds.includes(el?.his_payer_id)))
          .map((data) => ({
            payer_id: data?.payer_id,
            rcm_ref: response?.data?.patient?.rcm_ref,
            insurance_plan_id: data?.insurance_plan_id,
            is_new_born:
              payload?.is_new_born ??
              response?.data?.patient?.is_new_born ??
              false,
          }));

        const results = await Promise.all(
          payloads.map((data) =>
            handleCheckPatientElibility(
              data,
              () => { },
              () => { }
            )
          )
        );
        const validResults = results.filter((r) => r);
        setEligibilityResData(validResults);
        setOpenEligibilityForm(true);
      }

      if (checkEligibility == false) {
        fromVisit
          ? navigate(
            returnDetail?.is_without_slot == true
              ? "/create-visit-without-slot"
              : "/create-visit",
            { state: returnDetail }
          )
          : navigate("/beneficiary", {
            state: { prevPayload: getBeneficiariesPayload },
          });
      }
    } catch (error) {
      setCheckEligibility(false);
      toast.error(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Error"
      );
      return rejectWithValue(error?.response?.data?.error || "Error");
    }
  }
);

export const deletePatient = createAsyncThunk(
  "deletePatient",
  async ({ payload, beneficiaryPayload, dispatch }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(`/v3/patient/delete`, payload);
      toast.success(response?.data?.message);
      if (dispatch) {
        dispatch(baseApi.util.invalidateTags(["Beneficiary"]));
      }
      if (beneficiaryPayload && dispatch) {
        dispatch(getPatientsData(beneficiaryPayload));
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Error"
      );
      return rejectWithValue(error?.response?.data?.error || "Error");
    }
  }
);

export const getPatientEMRReportsData = createAsyncThunk(
  "getPatientEMRReportsData",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        `/v3/emr/getReportByPatientId`,
        payload
      );
      return response.data;
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Error"
      );
      return rejectWithValue(error?.response?.data?.error || "Error");
    }
  }
);

export const getPatientDoctorNotesData = createAsyncThunk(
  "getDoctorNotesByPatientId",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        `/v3/emr/getDoctorNotesByPatientId`,
        payload
      );
      return response.data;
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Error"
      );
      return rejectWithValue(error?.response?.data?.error || "Error");
    }
  }
);

const initialState = {
  isGeneralSearch: false,
  isLoading: false,
  seletedPatientData: null,
  patientEmrReports: null,
  patientDoctorNotes: null,
};

const beneficiarySlice = createSlice({
  name: "beneficiary",
  initialState,
  reducers: {
    setSelectPatientData: (state, action) => {
      state.seletedPatientData = action.payload;
    },
    setIsGeneralSearch: (state, action) => {
      state.isGeneralSearch = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPatientsData.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getPatientsData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.patientsData = action.payload;
      })
      .addCase(getPatientsData.rejected, (state) => {
        state.isLoading = false;
        state.patientsData = null;
      })
      .addCase(getPatientEMRReportsData.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getPatientEMRReportsData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.patientEmrReports = action.payload;
      })
      .addCase(getPatientEMRReportsData.rejected, (state) => {
        state.isLoading = false;
        state.patientEmrReports = null;
      })
      .addCase(getPatientDoctorNotesData.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getPatientDoctorNotesData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.patientDoctorNotes = action.payload;
      })
      .addCase(getPatientDoctorNotesData.rejected, (state) => {
        state.isLoading = false;
        state.patientDoctorNotes = null;
      });
  },
});

export const { setSelectPatientData, setIsGeneralSearch } =
  beneficiarySlice.actions;
export default beneficiarySlice.reducer;
